import { useCatalogStore } from '@/store/catalogStore'

/**
 * Type ID = the catalog `target_device` / quality code for a stream.
 * Resolved from the live catalog store (stream_id → target_device).
 */
export const TYPE_ID_LABELS: Record<string, string> = {
  MB: 'Mobile',
  DT: 'Desktop',
  PC: 'Desktop',
  TB: 'Tablet',
  HD: 'HD',
  FHD: 'Full HD',
  UHD: '4K',
  SD: 'SD',
}

function lookupTargetDevice(streamId: string): string | null {
  const key = streamId.trim().toUpperCase()
  const streams = useCatalogStore.getState().streams
  for (const s of streams) {
    if (s.stream_id?.trim().toUpperCase() === key && s.target_device) {
      return s.target_device
    }
    // Also match type field like "SD-MB" when target_device empty
    if (s.stream_id?.trim().toUpperCase() === key && s.type) {
      const parts = s.type.split('-')
      return parts[parts.length - 1] ?? s.type
    }
  }
  return null
}

/** Raw deviceQuality code for a stream id, or null when uncatalogued. */
export function typeIdCodeForStream(streamId: string | null | undefined): string | null {
  if (!streamId) return null
  return lookupTargetDevice(streamId)
}

/** Human label for a deviceQuality code ("MB" → "Mobile"); passthrough if unknown. */
export function typeIdLabel(code: string | null | undefined): string | null {
  if (!code) return null
  const cc = code.trim().toUpperCase()
  return TYPE_ID_LABELS[cc] ?? code
}

/** One-step lookup: stream id → display label ("Mobile"), null when unknown. */
export function typeIdForStream(streamId: string | null | undefined): string | null {
  return typeIdLabel(typeIdCodeForStream(streamId))
}

/** Compact type token e.g. "SD-MB" from catalog type or composed fields. */
export function typeTokenForStream(streamId: string | null | undefined): string | null {
  if (!streamId) return null
  const key = streamId.trim().toUpperCase()
  const streams = useCatalogStore.getState().streams
  const row = streams.find((s) => s.stream_id?.trim().toUpperCase() === key)
  if (!row) return null
  if (row.type?.trim()) return row.type.trim()
  const device = row.target_device?.trim()
  return device || null
}
